import React from 'react';
import { useSelector } from 'react-redux';
import { InputGroup, Button, FloatingLabel, Row, Col } from 'react-bootstrap';
import { Formik, Form, useFormikContext } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { map } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'usehooks-ts';

import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrderStatuses, tOrderStatus } from '@models/Order';
import { FormikControl, FormikSelect } from '@components/Base';
import { mergeInitialValues } from '@utils/formik';
import { PolicyTypes, tPolicyType } from '@models/policyType';
import { InsuranceTypesEnum, tInsuranceTypeEnum } from '@models/InsuranceType';
import { useCompaniesFetch } from '@components/Feature/Company';
import { selectIsCustomer } from '@components/Auth';

type FormValues = {
  search: string;
  status: OrderStatuses | '';
  policyType: PolicyTypes | '';
  insuranceType: InsuranceTypesEnum | '';
  companyId: string;
}

const FormForm = () => {
  const { t } = useTranslation();
  const companies = useCompaniesFetch();
  const isCustomer = useSelector(selectIsCustomer);
  const [, setSearchParams] = useSearchParams();
  const { values } = useFormikContext<FormValues>();
  const { status, policyType, insuranceType, companyId } = values;

  useUpdateEffect(() => {
    setSearchParams(values);
  }, [status, policyType, insuranceType, companyId]);

  return (
    <Form>
      <Row>
        <Col xs={12} xl={4} className="mb-3">
          <InputGroup>
            <FloatingLabel label={t('insurancePolicy.search')} style={{ flex: '1 1 auto', width: '1%', minWidth: '0' }}>
              <FormikControl name="search" isValid={false} isInvalid={false} />
            </FloatingLabel>
            <Button type="submit">
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup>
        </Col>

        <Col xs={4} xl={2} className="mb-3">
          <FloatingLabel label={t('common:policyType')}>
            <FormikSelect name="policyType" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(PolicyTypes, (value,) => (
                <option key={value} value={value}>{tPolicyType(value)}</option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>

        <Col xs={4} xl={2} className="mb-3">
          <FloatingLabel label={t('common:insuranceType')}>
            <FormikSelect name="insuranceType" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(InsuranceTypesEnum, (value,) => (
                <option key={value} value={value}>{tInsuranceTypeEnum(value)}</option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>

        <Col xs={4} xl={2} className="mb-3">
          <FloatingLabel label={t('common:status')}>
            <FormikSelect name="status" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(OrderStatuses, (value,) => (
                <option key={value} value={value}>{tOrderStatus(value)}</option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>

        {!isCustomer && (
          <Col xs={4} xl={2} className="mb-3">
            <FloatingLabel label={t('common:companyName')}>
              <FormikSelect name="companyId" isValid={false} isInvalid={false}>
                <option value="">{t('common:all')}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </FormikSelect>
            </FloatingLabel>
          </Col>
        )}
      </Row >
    </Form>
  )
}

const PolicyControlForm = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams);
  const defaultValues: FormValues = { status: '', search: '', insuranceType: '', policyType: '', companyId: '' };
  const initialValues = mergeInitialValues(defaultValues, params);

  const handleSubmit = ({ search }: FormValues) => {
    setSearchParams({ search });
  }

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      <FormForm />
    </Formik >
  );
}

export { PolicyControlForm };
